import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import { PmsReportMeta } from '../models/pms-report-meta';
import {
   ReportRiskSummary,
   ReportRiskSummaryItem
} from '../models/report-risk-summary';
import { formatCurrency } from '../utils/report-formatting.utils';
import { getReportMeta, getRiskSummary } from '../utils/report-json.utils';

type RiskSummaryViewState = 'hidden' | 'risks' | 'success' | 'skipped' | 'failed';

@Component({
   selector: 'app-report-risk-summary',
   standalone: true,
   imports: [CommonModule],
   templateUrl: './report-risk-summary.component.html',
   styleUrls: ['./report-risk-summary.component.scss']
})
export class ReportRiskSummaryComponent implements OnChanges {
   @Input() reportData: unknown = null;

   riskSummary: ReportRiskSummary | null = null;
   reportMeta: PmsReportMeta | null = null;
   viewState: RiskSummaryViewState = 'hidden';
   isDetailsExpanded = false;

   ngOnChanges(changes: SimpleChanges): void {
      if (!changes['reportData']) {
         return;
      }

      this.riskSummary = getRiskSummary(this.reportData);
      this.reportMeta = getReportMeta(this.reportData);
      this.viewState = this.resolveViewState();
      this.isDetailsExpanded = false;
   }

   isVisible(): boolean {
      return this.viewState !== 'hidden';
   }

   isRiskState(): boolean {
      return this.viewState === 'risks';
   }

   getStateClass(): string {
      return `state-${this.viewState}`;
   }

   getStateTitle(): string {
      switch (this.viewState) {
         case 'risks':
            return 'Risk Summary';

         case 'success':
            return 'No risks identified';

         case 'skipped':
            return 'Risk Detection skipped';

         case 'failed':
            return 'Risk Detection unavailable';

         default:
            return '';
      }
   }

   getStateIconClass(): string {
      switch (this.viewState) {
         case 'risks':
            return 'pi pi-exclamation-triangle';

         case 'success':
            return 'pi pi-check-circle';

         case 'skipped':
            return 'pi pi-info-circle';

         case 'failed':
            return 'pi pi-exclamation-circle';

         default:
            return 'pi pi-info-circle';
      }
   }

   getStateMessage(): string {
      const backendMessage = this.reportMeta?.RiskEvaluationMessage?.trim();

      if (backendMessage) {
         return backendMessage;
      }

      switch (this.viewState) {
         case 'success':
            return 'No risks were identified for this report.';

         case 'skipped':
            return 'Risk Detection was not evaluated for this report.';

         case 'failed':
            return 'Risk Detection could not be completed for this report.';

         default:
            return '';
      }
   }

   getCombinedSummary(): string {
      return this.riskSummary?.CombinedSummary?.trim() ?? '';
   }

   getCombinedRecommendedAction(): string {
      return this.riskSummary?.CombinedRecommendedAction?.trim() ?? '';
   }

   getRiskCount(): number | null {
      return this.getFiniteNumber(
         this.riskSummary?.RiskCount ?? this.reportMeta?.RiskCount
      );
   }

   getAffectedRowCount(): number | null {
      return this.getFiniteNumber(this.riskSummary?.AffectedRowCount);
   }

   getTotalRiskValue(): number | null {
      return this.getFiniteNumber(this.riskSummary?.TotalRiskValue);
   }

   getHighestSeverity(): string {
      return this.reportMeta?.HighestSeverity?.trim() ?? '';
   }

   getItems(): ReportRiskSummaryItem[] {
      return this.riskSummary?.Items ?? [];
   }

   hasRiskCount(): boolean {
      return this.getRiskCount() !== null;
   }

   hasAffectedRowCount(): boolean {
      return this.getAffectedRowCount() !== null;
   }

   hasTotalRiskValue(): boolean {
      return this.getTotalRiskValue() !== null;
   }

   hasHighestSeverity(): boolean {
      return this.getHighestSeverity().length > 0;
   }

   hasDetails(): boolean {
      return this.getItems().length > 0;
   }

   toggleDetails(): void {
      this.isDetailsExpanded = !this.isDetailsExpanded;
   }

   formatRiskValue(value: unknown): string {
      const amount = this.getFiniteNumber(value);

      return amount === null ? '' : formatCurrency(amount);
   }

   hasItemNumber(value: unknown): boolean {
      return this.getFiniteNumber(value) !== null;
   }

   getItemTitle(item: ReportRiskSummaryItem): string {
      return item.Name?.trim() || item.RiskCode?.trim() || 'Risk item';
   }

   getSeverityClass(severity: string | null | undefined): string {
      const normalisedSeverity = severity?.trim().toLowerCase() ?? '';

      switch (normalisedSeverity) {
         case 'critical':
         case 'high':
         case 'medium':
         case 'low':
         case 'info':
            return `severity-${normalisedSeverity}`;

         default:
            return 'severity-neutral';
      }
   }

   trackByRiskItem(index: number, item: ReportRiskSummaryItem): string {
      return item.RiskCode?.trim() || item.Name?.trim() || `risk-item-${index}`;
   }

   private resolveViewState(): RiskSummaryViewState {
      const evaluationStatus =
         this.reportMeta?.RiskEvaluationStatus?.trim().toUpperCase() ?? '';

      if (evaluationStatus === 'SKIPPED') {
         return 'skipped';
      }

      if (
         evaluationStatus === 'FAILED' ||
         evaluationStatus === 'FAILURE' ||
         evaluationStatus === 'ERROR'
      ) {
         return 'failed';
      }

      const riskCount = this.getRiskCount();

      if ((riskCount !== null && riskCount > 0) || this.getItems().length > 0) {
         return 'risks';
      }

      if (
         riskCount === null &&
         this.riskSummary &&
         this.hasUsefulRiskSummaryContent()
      ) {
         return 'risks';
      }

      if (
         evaluationStatus === 'SUCCESS' ||
         (this.riskSummary !== null && riskCount === 0)
      ) {
         return 'success';
      }

      return 'hidden';
   }

   private hasUsefulRiskSummaryContent(): boolean {
      return (
         this.getCombinedSummary().length > 0 ||
         this.getCombinedRecommendedAction().length > 0 ||
         this.getAffectedRowCount() !== null ||
         this.getTotalRiskValue() !== null
      );
   }

   private getFiniteNumber(value: unknown): number | null {
      return typeof value === 'number' && Number.isFinite(value) ? value : null;
   }
}
